import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const INTERESTS = [
  "Chevalet Avis Google",
  "Menu Digital QR",
  "Bornes & Plaquettes",
  "Gestion de Fiche Google",
  "Offre Présence",
  "Offre Croissance",
  "Offre Grands Comptes",
  "Projet sur mesure",
  "Autre",
];

export const Contact = () => {
  const [params] = useSearchParams();
  const sujet = params.get("sujet") || "";

  const [form, setForm] = useState({ name: "", email: "", company: "", interest: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (sujet) {
      setForm((f) => ({
        ...f,
        interest: sujet,
        message: f.message || `Bonjour, je suis intéressé(e) par : ${sujet}.\n\n`,
      }));
    }
  }, [sujet]);

  const options = sujet && !INTERESTS.includes(sujet) ? [sujet, ...INTERESTS] : INTERESTS;

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", {
        name: form.name,
        email: form.email,
        company: form.company || null,
        interest: form.interest || null,
        message: form.message,
      });
      setSent(true);
      toast.success("Demande envoyée ! On vous recontacte rapidement.");
      setForm({ name: "", email: "", company: "", interest: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-background border border-border px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent transition-colors";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-20 sm:py-24 md:py-32 relative overflow-hidden scroll-mt-24"
    >
      <div className="absolute -bottom-20 -right-20 w-[35rem] h-[35rem] bg-warm/10 blur-3xl rounded-full pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-10 lg:gap-16 items-start relative z-10">
        <div>
          <p className="font-mono text-xs tracking-[0.25em] uppercase text-warm mb-4">Une prise de contact ?</p>
          <h2 className="font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl">
            Parlons de votre <span className="text-warm">enseigne</span>.
          </h2>
          <p className="mt-6 text-muted-foreground leading-relaxed max-w-md">
            Une marque nationale, un réseau de points de vente, un restaurant ?
            On construit une solution sur mesure. Écrivez-nous, réponse sous 24h.
          </p>
          <a
            href="mailto:Gmpdeveloppement@gmail.com"
            data-testid="contact-email-link"
            className="mt-10 inline-flex items-center gap-3 border border-border px-5 py-3 hover:border-warm/50 transition-colors group"
          >
            <Mail size={18} className="text-warm" />
            <span className="text-sm group-hover:text-foreground text-foreground/80 break-all">Gmpdeveloppement@gmail.com</span>
          </a>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="bg-surface border border-border p-6 sm:p-8 w-full"
        >
          {sent ? (
            <div data-testid="contact-success" className="flex flex-col items-center text-center py-12">
              <CheckCircle2 size={48} className="text-warm mb-5" />
              <h3 className="font-heading font-semibold text-2xl mb-2">Message bien reçu</h3>
              <p className="text-muted-foreground text-sm mb-6">Notre équipe revient vers vous très vite.</p>
              <button
                onClick={() => setSent(false)}
                data-testid="contact-reset"
                className="text-warm text-sm hover:underline"
              >
                Envoyer une autre demande
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-2">Nom *</label>
                  <input required data-testid="contact-input-name" value={form.name} onChange={update("name")} placeholder="Votre nom" className={inputCls} />
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-2">Email *</label>
                  <input required type="email" data-testid="contact-input-email" value={form.email} onChange={update("email")} placeholder="vous@entreprise.fr" className={inputCls} />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-2">Entreprise</label>
                  <input data-testid="contact-input-company" value={form.company} onChange={update("company")} placeholder="Nom de l'enseigne" className={inputCls} />
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-2">Sujet</label>
                  <select data-testid="contact-input-interest" value={form.interest} onChange={update("interest")} className={inputCls}>
                    <option value="">Sélectionner…</option>
                    {options.map((i) => (
                      <option key={i} value={i}>{i}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-2">Message *</label>
                <textarea required rows={4} data-testid="contact-input-message" value={form.message} onChange={update("message")} placeholder="Parlez-nous de votre projet…" className={`${inputCls} resize-none`} />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-button"
                className="w-full bg-accent text-background font-semibold px-6 py-3.5 flex items-center justify-center gap-2 hover:bg-accent-hover transition-colors disabled:opacity-60"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                {loading ? "Envoi…" : "Envoyer ma demande"}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};
